import { CountryCode } from '@glow/common'
import { is, List, Map, OrderedMap, Seq } from 'immutable'
import { lruMemoize } from 'reselect'
import { allDepartmentsFromStateSelector } from '../selectors/departmentsSelector'
import { Department, DepartmentGroup, DepartmentGroupIdType, DepartmentIdType } from '../types/coreEntitiesTypes'
import { ImmutableMap } from '../types/immutableTypes'
import { AppStateType } from './appStateReduxStore'

export enum DepartmentType {
  Courier = 'courier',
  Express = 'express',
  HD = 'hd',
  Parcel = 'parcel',
  Posten = 'posten',
  Other = 'other',
  H2 = 'h2'
}

/**
 * Parcel departments in Norway, the population several features are limited to. A static property
 * of the department itself: deliberately not derived from the user's department selection, which
 * would move the limit around as they change what is selected.
 */
export const isParcelNorwayDepartment = (department: Department | undefined): boolean =>
  department?.get('departmentType') === DepartmentType.Parcel &&
  department?.get('country')?.toUpperCase() === CountryCode.NO

export enum DepartmentGroupType {
  DEFAULT = 'DEFAULT',
  FOLLOW_UP = 'FOLLOW_UP',
  INVOICING = 'INVOICING',
  PLANNING = 'PLANNING',
  PREADVICE = 'PREADVICE',
  REGION = 'REGION',
  SEARCH = 'SEARCH'
}

const ALL_PLANNER_PAGES = '*'

const groupTypeAllowedPlannerPages: Record<DepartmentGroupType, string[] | typeof ALL_PLANNER_PAGES> = {
  [DepartmentGroupType.DEFAULT]: ALL_PLANNER_PAGES,
  [DepartmentGroupType.FOLLOW_UP]: ['preadvice-followup', 'shipments-search', 'shipments', 'reports', 'manage-returns'],
  [DepartmentGroupType.INVOICING]: ['invoicing', 'invoicing-new', 'shipments-search', 'shipments', 'export'],
  [DepartmentGroupType.PLANNING]: [
    'coordinates',
    'dispatch',
    'live2',
    'planning',
    'routes-overview',
    'shipments',
    'shipments-search'
  ],
  [DepartmentGroupType.REGION]: ['reports', 'shipments-search', 'shipments', 'export'],
  [DepartmentGroupType.PREADVICE]: [], // should not have any access, reserved for the preadvice rule admin page
  [DepartmentGroupType.SEARCH]: ['shipments-search', 'shipments']
}

const groupTypeDefaultPlannerLanding: Record<DepartmentGroupType, string> = {
  [DepartmentGroupType.DEFAULT]: 'shipments-search',
  [DepartmentGroupType.FOLLOW_UP]: 'shipments-search',
  [DepartmentGroupType.INVOICING]: 'invoicing-new',
  [DepartmentGroupType.PLANNING]: 'planning',
  [DepartmentGroupType.PREADVICE]: 'shipments-search',
  [DepartmentGroupType.REGION]: 'reports',
  [DepartmentGroupType.SEARCH]: 'shipments-search'
}

const GROUP_SUPPORTED_PLANNER_PAGES = [
  'shipments',
  'invoicing',
  'map',
  'dispatch',
  'export',
  'planning',
  'live2',
  'preadvice-followup',
  'rm',
  'pickup-orders',
  'unresolved',
  'coordinates',
  'routes-overview',
  'reports'
]
const GROUP_ID_PATTERN = /^group_(\d+)$/

// TODO(olav): Use `${DepartmentType}` when we have TypeScript@^4.1.
export type DepartmentTypes = 'courier' | 'express' | 'hd' | 'parcel' | 'posten' | 'other' | 'h2'

interface Props {
  match: {
    params: {
      departmentId?: string
    }
  }
}

interface ReturnValue {
  departmentOrGroupId: string
  isDepartmentGroup: boolean
  allDepartmentsCount: number
  allGroups: Map<number, DepartmentGroup>
  selectedDepartmentIds: List<DepartmentIdType>
  groupsAndDepartments: OrderedMap<string, List<Map<string, string | number>>>
  departmentsAndGroupsCount: number
  departmentIdsInSameGroup: List<DepartmentIdType>
  allDepartments: Map<number, Department>
}

export const isGroupId = (id: DepartmentIdType | string | undefined): id is string =>
  typeof id === 'string' && GROUP_ID_PATTERN.test(id)

export const toDepartmentGroupType = (type?: string): DepartmentGroupType =>
  Object.values(DepartmentGroupType).includes(type as DepartmentGroupType)
    ? (type as DepartmentGroupType)
    : DepartmentGroupType.DEFAULT

export const departmentGroupTypeFromGroup = (group?: DepartmentGroup): DepartmentGroupType =>
  toDepartmentGroupType(group?.get('type'))

export const departmentGroupIdFromStringId = (groupId: string): DepartmentGroupIdType | undefined => {
  const match = groupId.match(GROUP_ID_PATTERN)
  return match ? Number.parseInt(match[1], 10) : undefined
}

export const getDepartmentGroupId = (id: string) => departmentGroupIdFromStringId(id)

export const getPlannerPageKeyFromPath = (pathname: string): string | undefined => {
  const pathnameWithoutHash = pathname.split('#')[0]
  const normalizedPathname = pathnameWithoutHash.split('?')[0]
  const pathSegments = normalizedPathname.split('/').filter((segment) => segment !== '')

  if (pathSegments.length === 0) {
    return undefined
  }

  const plannerPathSegments = pathSegments[0] === 'planner' ? pathSegments.slice(1) : pathSegments

  if (
    plannerPathSegments.length >= 2 &&
    (isGroupId(plannerPathSegments[0]) || !Number.isNaN(Number(plannerPathSegments[0])))
  ) {
    return plannerPathSegments[1]
  }

  return plannerPathSegments[0]
}

export const isGroupTypeAllowedForPlannerPath = (type: DepartmentGroupType | undefined, pathname: string): boolean => {
  const plannerPageKey = getPlannerPageKeyFromPath(pathname)
  if (!plannerPageKey) {
    return true
  }

  const allowedPlannerPages = groupTypeAllowedPlannerPages[toDepartmentGroupType(type)]
  return allowedPlannerPages === ALL_PLANNER_PAGES || allowedPlannerPages.includes(plannerPageKey)
}

export const groupTypeHasAccessToPlannerPages = (type: DepartmentGroupType | undefined): boolean => {
  const allowedPlannerPages = groupTypeAllowedPlannerPages[toDepartmentGroupType(type)]
  return allowedPlannerPages === ALL_PLANNER_PAGES || allowedPlannerPages.length > 0
}

export const filterGroupsAllowedForPlannerPath = (
  allDepartmentGroups: Map<DepartmentGroupIdType, DepartmentGroup>,
  pathname: string
): Map<DepartmentGroupIdType, DepartmentGroup> =>
  allDepartmentGroups.filter((group) =>
    isGroupTypeAllowedForPlannerPath(departmentGroupTypeFromGroup(group), pathname)
  ) as Map<DepartmentGroupIdType, DepartmentGroup>

export const getDefaultPlannerLandingForGroupType = (type: DepartmentGroupType | undefined) =>
  groupTypeDefaultPlannerLanding[toDepartmentGroupType(type)]

export function plannerPagesSupportsGroups(url: string) {
  const plannerPageKey = getPlannerPageKeyFromPath(url)
  if (!plannerPageKey) {
    return false
  }

  return GROUP_SUPPORTED_PLANNER_PAGES.some(
    (supportedPage) => plannerPageKey === supportedPage || plannerPageKey.startsWith(`${supportedPage}-`)
  )
}

export function departmentsAndDepartmentGroupsFromState(
  state: AppStateType,
  props?: Props,
  supportsGroups = false
): ReturnValue {
  const allDepartments = allDepartmentsFromStateSelector(state)
  const allDepartmentGroups = (state.getIn(['entities', 'departmentGroups']) as Map<number, DepartmentGroup>) || Map()
  return departmentsAndDepartmentGroups(
    allDepartments,
    allDepartmentGroups,
    props?.match.params.departmentId ?? '',
    supportsGroups
  )
}

// Called from mapStateToProps on every store update; a new result each time would re-render every connected page
const departmentsAndDepartmentGroups = lruMemoize(
  (
    allDepartments: Map<DepartmentIdType, Department>,
    allDepartmentGroups: Map<DepartmentGroupIdType, DepartmentGroup>,
    departmentOrGroupId: string,
    supportsGroups: boolean
  ): ReturnValue => {
    let selectedDepartmentIds = Seq.Indexed<DepartmentIdType>([])
    let departmentIdsInSameGroup = Seq.Indexed<DepartmentIdType>([])

    const isDepartmentGroup = departmentOrGroupId && isGroupId(departmentOrGroupId)
    if (isDepartmentGroup) {
      const departmentGroupId = getDepartmentGroupId(departmentOrGroupId)
      selectedDepartmentIds =
        (allDepartmentGroups.getIn([departmentGroupId, 'departments']) as Seq.Indexed<DepartmentIdType>) ||
        Seq.Indexed<DepartmentIdType>()
      departmentIdsInSameGroup = selectedDepartmentIds
    } else if (departmentOrGroupId) {
      const departmentId = Number.parseInt(departmentOrGroupId, 10)
      selectedDepartmentIds = Seq.Indexed([departmentId])
      departmentIdsInSameGroup = Seq.Indexed([departmentId])
    }

    let groupsAndDepartments = OrderedMap<string, List<Map<string, string | number>>>()
    if (supportsGroups && allDepartmentGroups?.count() > 0) {
      groupsAndDepartments = groupsAndDepartments.set(
        'Groups',
        allDepartmentGroups
          .map((group) =>
            Map({
              name: group.get('name'),
              id: `group_${group.get('id')}`
            })
          )
          .toList()
      )
    }
    groupsAndDepartments = groupsAndDepartments.set(
      'Departments',
      allDepartments
        .map((dep) =>
          Map({
            name: dep.get('name'),
            id: dep.get('id')
          })
        )
        .toList()
    )
    const departmentsAndGroupsCount = allDepartmentGroups.count() + allDepartments.count()
    return {
      departmentOrGroupId,
      isDepartmentGroup: !!isDepartmentGroup,
      allDepartmentsCount: allDepartments?.count() || 0,
      allGroups: allDepartmentGroups,
      selectedDepartmentIds: selectedDepartmentIds.toList(),
      groupsAndDepartments,
      departmentsAndGroupsCount,
      departmentIdsInSameGroup: departmentIdsInSameGroup.toList(),
      allDepartments
    }
  },
  { equalityCheck: is, maxSize: 10 }
)

export function getSelectedDepartmentIds(
  allDepartmentGroups: Map<DepartmentGroupIdType, DepartmentGroup>,
  departmentOrGroupId: string
) {
  let selectedDepartmentIds = Seq.Indexed<DepartmentIdType>([])

  const isDepartmentGroup = departmentOrGroupId && isGroupId(departmentOrGroupId)
  if (isDepartmentGroup) {
    const departmentGroupId = getDepartmentGroupId(departmentOrGroupId)
    selectedDepartmentIds =
      (allDepartmentGroups.getIn([departmentGroupId, 'departments']) as Seq.Indexed<DepartmentIdType>) ||
      Seq.Indexed<DepartmentIdType>()
  } else if (departmentOrGroupId) {
    // A hand-typed or otherwise malformed :departmentId must not become a NaN id.
    const departmentId = Number(departmentOrGroupId)
    selectedDepartmentIds = Number.isNaN(departmentId) ? selectedDepartmentIds : Seq.Indexed([departmentId])
  }

  return selectedDepartmentIds.toList()
}

export const eligibleForCreatingShipmentLabels = (departmentAlystraId?: string): boolean =>
  departmentAlystraId === 'OSLO DEKK & DELER'

export enum OrderBillingType {
  Apollo = 'apollo',
  H2 = 'h2',
  Pickup = 'pickup',
  InternalTrade = 'internaltrade'
}

export interface OrderBillingInfoProps {
  type: OrderBillingType | ''
  customerNumber: number | ''
  customerNumberSe: number | ''
  customerNumberNo: number | ''
  customerNumberDk: number | ''
}

export interface OrderBillingInfo extends ImmutableMap<OrderBillingInfoProps> {}
